import dayjs from 'dayjs';
import { shopDueDate } from '../../ShopShipDate';

export type KittingQueueClass =
	| 'in_progress'
	| 'late'
	| 'hard_job'
	| 'ready'
	| 'completed';

export type KittingQueueView = 'All' | 'NotStarted' | 'InProgress' | 'Late';

export type KittingPrioritization = 'readiness' | 'due_date' | 'scheduler';

export interface KittingQueueSummary {
	all: number;
	not_started: number;
	in_progress: number;
	late: number;
	hard_job: number;
	completed: number;
	ready: number;
}

/**
 * The view the tablet opens on. Every class, in one ranked list, matching
 * the saw queue: show all of them and let the operator filter down.
 */
export const DEFAULT_QUEUE_VIEW: KittingQueueView = 'All';

/**
 * Scheduler queue: the kitting-task start the scheduler already assigned.
 * Class tags still identify a kit that is ready, late, hard, or in progress;
 * they do not move it ahead of work the scheduler sequenced sooner (PTB-7105).
 * Production first remains available when an operator wants that rank.
 */
export const DEFAULT_PRIORITIZATION: KittingPrioritization = 'scheduler';

/**
 * Overlay only. The class tag is the source of truth for hard jobs; this
 * number is used when lateness or a started kit already took `queue_class`
 * and the card still needs to say the part is difficult.
 */
export const HIGH_DIFFICULTY = 8;
export const HIGH_QTY = 25;

/**
 * Quoted turnaround in days. The catalog still sells expedite ~5 and
 * standard 14, but floor quotes cluster at 21 (138 of 282 rows with a
 * lead time in fastlane_dev; the next buckets are 23 → 50 and 20 → 29).
 * At 15 the chip fired on 97% of cards and told the operator nothing.
 * 21 is the usual quote, not a long one, so the bar sits above that
 * cluster: 22+ is the tail that should be pulled forward.
 */
export const LONG_LEAD_DAYS = 22;

interface ClassLabel {
	label: string;
	color: string;
}

export const QUEUE_CLASS_LABELS: Record<KittingQueueClass, ClassLabel> = {
	in_progress: { label: 'Finish this kit', color: 'gold' },
	late: { label: 'Late', color: 'volcano' },
	hard_job: { label: 'Hard job', color: 'magenta' },
	ready: { label: 'Ready to kit', color: 'blue' },
	completed: { label: 'Kitted', color: 'green' },
};

export const isKittingQueueClass = (
	value: string | null | undefined
): value is KittingQueueClass =>
	value === 'in_progress' ||
	value === 'late' ||
	value === 'hard_job' ||
	value === 'ready' ||
	value === 'completed';

export const isKittingQueueView = (
	value: string | null | undefined
): value is KittingQueueView =>
	value === 'All' ||
	value === 'NotStarted' ||
	value === 'InProgress' ||
	value === 'Late';

export const isKittingPrioritization = (
	value: string | null | undefined
): value is KittingPrioritization =>
	value === 'readiness' || value === 'due_date' || value === 'scheduler';

export const parseKittingQueueView = (
	value: string | null | undefined
): KittingQueueView =>
	isKittingQueueView(value) ? value : DEFAULT_QUEUE_VIEW;

export const parseKittingPrioritization = (
	value: string | null | undefined
): KittingPrioritization =>
	isKittingPrioritization(value) ? value : DEFAULT_PRIORITIZATION;

/**
 * Chips a kitting card carries besides its class: whether the material is
 * already cut, and why this kit should be pulled forward even when the class
 * already named something else.
 *
 * Material ready reads `material_prep_status`, which the server sets to
 * Completed only once every reservation on the line item is Cut. The class
 * cannot say this: Ready to kit only means not started, not late and not
 * hard, and the board lists a kit as soon as its material is selected
 * (PTB-7105). A kitted card does not need it.
 *
 * Hard job as a class is owned by the server. This overlay only fires when
 * `queue_class` is already late or in_progress, so a ready card cannot also
 * read "Hard job" if the client and server thresholds ever drift.
 */
export const kittingChips = (item: {
	quantity?: number | null;
	run_difficulty?: number | null;
	lead_time?: string | number | null;
	required_partner_ship_date?: string | null;
	shop_due_date?: string | null;
	queue_class?: string | null;
	material_prep_status?: string | null;
}): { label: string; color: string }[] => {
	const chips: { label: string; color: string }[] = [];
	if (
		item.material_prep_status === 'Completed' &&
		item.queue_class !== 'completed'
	) {
		chips.push({ label: 'Material ready', color: 'green' });
	}
	if ((item.quantity ?? 0) >= HIGH_QTY) {
		chips.push({ label: 'High qty', color: 'geekblue' });
	}
	if (
		(item.queue_class === 'late' || item.queue_class === 'in_progress') &&
		(item.run_difficulty ?? 0) >= HIGH_DIFFICULTY
	) {
		chips.push({ label: 'Hard job', color: 'magenta' });
	}
	const leadDays = Number(item.lead_time);
	if (Number.isFinite(leadDays) && leadDays >= LONG_LEAD_DAYS) {
		chips.push({ label: 'Long-lead — start early', color: 'orange' });
	}
	if (shopDueDate(item) && item.queue_class !== 'late') {
		const diff = dayjs(shopDueDate(item))
			.startOf('day')
			.diff(dayjs().startOf('day'), 'day');
		if (diff > 0 && diff <= 2) {
			chips.push({ label: 'Due soon', color: 'gold' });
		}
	}
	return chips;
};
